import React, { useContext, useEffect, useState } from 'react'
import Input from '../../../Forms/Input'
import Button from '../../../Forms/Button'
import Result, { EResult } from './components/Result'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { EMAIL_REGX } from '../../../../constants/regex'
import SidebarActionLink from './components/SibebarActionLink'
import { ReactComponent as CallIcon } from './icons/phone-red.svg'
import { ReactComponent as ChatIcon } from './icons/chat-red.svg'
import { ReactComponent as TextIcon } from './icons/text-red.svg'
import CortLogoImage from './icons/cort-logo.png'
import useDetectDevice from '../../../../hooks/useDetectDevice'
import { apiClient } from '../../../../helpers/api'
import { isDebugMode, objectCopy, parseBoolean } from '../../../../helpers/general'
import { captureEvent } from '../../../../analytics'
import { analyticsEvents } from '../../../../analytics/events'
import { useStore } from '../../../../store'
import { AppConfigContext } from '../../../../context/AppConfig'
import VendorLogo from '../../../VendorLogo'
import { getHumanLabelBathrooms, getHumanLabelBedrooms } from '../../../../helpers/line'
import { sha1 } from 'js-sha1'
import PropertyLogo from '../../../PropertyLogo'
// Icons
import { ReactComponent as UserIcon } from './icons/user.svg'
import { variants } from '../../../../constants/variant'
import VariantWrapper from '../../../VariantWrapper'

export async function submitCortForm(values, cb, err, skipEvents = false) {
  const { email, last_name, first_name, comments, phone, ...extra } = values

  captureEvent(analyticsEvents.CORT_SUBMIT_TAPPED)

  const payload = {
    email,
    lastName: last_name,
    firstName: first_name,
    ...(comments ? { comments: comments } : {}),
    ...(phone ? { phoneNumber: String(phone) } : {}),
    leadPageName: 'CORT Widget',
    sparx_user_id: sha1(`${first_name}${last_name}${email}`),
    ...extra,
  }

  if (isDebugMode()) {
    console.log(('CORT SUBMIT VALUES', values))
  }

  try {
    await apiClient.post(`/api/cort/lead`, payload)
    if (!skipEvents) {
      captureEvent(analyticsEvents.SUBMIT_SUCCEEDED)
    }
    cb(EResult.SUCCESS)
  } catch (error) {
    if (!skipEvents) {
      captureEvent(analyticsEvents.SUBMIT_FAILED)
    }
    err(EResult.ERROR)
  }
}

export function PrivacyContent({ className = '' }) {
  const { isSWApp } = useContext(AppConfigContext)
  const { data } = useStore()

  function getPhoneNumber() {
    if (isSWApp) {
      return '888-1212-1212'
    } else {
      return '888-360-2678'
    }
  }

  return (
    <div className={`text-[12px] text-[#3D4256] opacity-50 flex items-center justify-center ${className}`}>
      <a
        href={data?.selectedPackage?.vendor?.privacy_policy_url}
        target="_blank"
        rel="noreferrer"
        onClick={() => {
          captureEvent(analyticsEvents.CORT_PRIVACY_TAPPED)
        }}
      >
        Privacy Policy
      </a>
      <div className="mx-[3px]">
        | {getPhoneNumber()} {!isSWApp ? '|' : ''}
      </div>
      {!isSWApp && (
        <a
          href="https://www.cort.com"
          rel="noreferrer"
          target="_blank"
          onClick={() => {
            captureEvent(analyticsEvents.CORT_WEBSITE_TAPPED)
          }}
        >
          cort.com
        </a>
      )}
    </div>
  )
}

export function useCortForm(onSubmit) {
  const validationSchema = Yup.object().shape({
    first_name: Yup.string().required(`Required field`),
    last_name: Yup.string().required(`Required field`),
    email: Yup.string().matches(EMAIL_REGX, 'Invalid email address').required(`Required field`),
    phone: Yup.string(),
  })

  const formik = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      comments: '',
    },
    validateOnChange: false,
    validateOnBlur: true,
    validationSchema,
    // validateOnMount:true,
    onSubmit,
  })
  return formik
}

function ContactForm({ onClose = () => {}, mobile = false, onResult = () => {} }) {
  const { setFormData, data } = useStore()
  const { isSWApp, isPDApp, external_property_id } = useContext(AppConfigContext)

  const [result, setResult] = useState()
  const [queryConfigParams, setQueryConfigParams] = useState({})
  const [loading, setLoading] = useState(false)
  useEffect(() => {
    const queryString = window.location.search

    // Parse the query string
    const urlParams = new URLSearchParams(queryString)

    const queryParamHideChatButton = urlParams.get('hide_chat_button')

    // Example: Get the value of a specific parameter, e.g., 'paramName'
    const [callUrl, smsUrl, chatUrl, hideChatButton] = [
      urlParams.get('call_url'),
      urlParams.get('sms_url'),
      urlParams.get('chat_url'),
      parseBoolean(queryParamHideChatButton !== null ? queryParamHideChatButton : true),
    ]

    setQueryConfigParams({
      callUrl,
      smsUrl,
      chatUrl,
      hideChatButton,
    })
  }, [])

  async function onSubmit() {
    setLoading(true)

    if (isSWApp || isPDApp) {
      onResult(EResult.SUCCESS)
      setResult(EResult.SUCCESS)
      setLoading(false)
      return
    }

    await submitCortForm(
      {
        ...formik.values,
        ...{
          package_name: data.selectedPackage.display_name,
          package_id: data.selectedPackage.id,
          line_id: data.selectedLine.id,
          line_name: data.selectedLine.name,
          package_starting_price: data.selectedPackage.price,
          ...(external_property_id ? { external_property_id } : {}),
          spec: [
            getHumanLabelBedrooms(data.selectedLine),
            getHumanLabelBathrooms(data.selectedLine),
            data.selectedLine.size_to_display ? `${data.selectedLine.size_to_display} ft sq` : '',
          ]
            .filter((val) => !!val)
            .join(' · '),
          link: `${window.location.href}&line_id=${data.selectedLine.id}&package_id=${data.selectedPackage.id}`,
        },
      },
      () => {
        onResult(EResult.SUCCESS)
        setResult(EResult.SUCCESS)
        setLoading(false)
      },
      () => {
        onResult(EResult.ERROR)
        setResult(EResult.ERROR)
        setLoading(false)
      },
    )
  }

  const formik = useCortForm(onSubmit)

  const { isMobile, isDesktop } = useDetectDevice()

  useEffect(() => {
    if (isMobile && mobile) {
      captureEvent(analyticsEvents.CORT_FORM_VIEWED)
    }
  }, [isMobile, mobile])

  useEffect(() => {
    setFormData(objectCopy(formik.values))
  }, [formik.values, setFormData])

  function getTitle() {
    if (isSWApp) {
      return 'Furnish Your New Apartment Your Way'
    } else {
      return 'Furnish your future apartment on your terms'
    }
  }

  function getSubTitle() {
    if (isSWApp) {
      return 'Set up your apartment with turnkey packages for rent or purchase.'
    } else {
      return 'Make your apartment Move-In Ready® with CORT, our preferred furniture provider.'
    }
  }

  const chatAccountId = (data?.property?.external_properties || [])[0]?.account_id

  return (
    <>
      <div className={`flex portrait:block lg:block ${isPDApp ? 'justify-center' : ''}`}>
        {isSWApp && (
          <>
            <div className="pr-[24px] mt-[28px] portrait:mt-[32px] lg:mt-[32px] flex justify-center">
              <VendorLogo className="!h-[27px]" />
            </div>
            <div className="ml-[20px] mt-[-8px] portrait:ml-[0] lg:ml-[0] portrait:mt-[0] lg:mt-[0]">
                  <div className="text-[18px] leading-[23px] font-semibold mt-[28px]">{getTitle()}</div>
                  <div className="mt-[2px] font-normal text-[14px]">{getSubTitle()}</div>
                </div>
          </>
        )}
        {!isSWApp && (
          <>
            {isPDApp ? (
              <>
                <div className="flex justify-center  items-center">
                  <img src={CortLogoImage} alt="cort" className="h-[72px]" />
                  <div className="ml-[16px]">
                    <div className="hidden lg:block">
                      <PropertyLogo />
                    </div>
                    <div className="lg:hidden">
                      <PropertyLogo />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex portrait:block lg:block items-center  lg:mt-0">
                <div className="flex justify-center">
                  <img src={CortLogoImage} alt="cort" className="h-[72px]" />
                </div>
                <div className="ml-[20px] mt-[-15px] portrait:ml-[0] lg:ml-[0] portrait:mt-[0] lg:mt-[0]">
                  <div className="text-[18px] leading-[23px] font-semibold mt-[28px]">{getTitle()}</div>
                  <div className="mt-[2px] font-normal text-[14px]">{getSubTitle()}</div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {isPDApp && (
        <div
          className={`mt-[-8px] portrait:ml-[0] lg:ml-[0]  ${
            isPDApp ? 'portrait:mt-[50px] lg:mt-[0px]' : 'portrait:mt-[0] lg:mt-[0]'
          }`}
        >
          <div className="text-[18px] leading-[23px] font-semibold mt-[28px]">{getTitle()}</div>
          <div className="mt-[2px] font-normal text-[14px]">{getSubTitle()}</div>
        </div>
      )}
      <form onSubmit={formik.handleSubmit}>
        <div
          className={`grid grid-cols-3 portrait:grid-cols-2 lg:grid-cols-2 gap-[14px] my-[16px] portrait:my-[24px] lg:!my-[24px] ${
            loading ? 'opacity-50 pointer-events-none' : ''
          }`}
        >
          <Input
            name="first_name"
            value={formik.values.first_name}
            onChange={formik.handleChange}
            required
            placeholder="First Name*"
            className="order-1"
          />
          <Input
            name="last_name"
            value={formik.values.last_name}
            onChange={formik.handleChange}
            required
            placeholder="Last Name*"
            className="order-2"
          />
          <Input
            name="phone"
            value={formik.values.phone}
            onChange={formik.handleChange}
            placeholder="Phone No."
            className="col-span-1 portrait:col-span-2 lg:col-span-2 order-4 portrait:order-3 lg:order-4"
          />
          <div className="col-span-1 portrait:col-span-2 lg:col-span-2 order-3 portrait:order-4 lg:order-4">
            <Input
              name="email"
              value={formik.values.email}
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              required
              placeholder="Email*"
              className="w-full"
            />
            {formik.errors.email && <div className="text-[red] text-[12px] font-bold mt-2">{formik.errors.email}</div>}
          </div>
          <Input
            name="comments"
            value={formik.values.comments}
            onChange={formik.handleChange}
            placeholder="Comments"
            className="col-span-2 order-5"
          />
        </div>
        <div>
          <Button
            type="submit"
            loading={loading}
            loadingText="Processing ..."
            className={`${isPDApp ? '!bg-black' : ''} !text-[16px] font-medium w-full`}
          >
            {isPDApp ? 'Contact Property' : 'Submit'}
          </Button>
        </div>
      </form>
          <VariantWrapper hide={[variants.of, variants.br, variants.sf, variants.sw]}> 
  <div className='mt-[22px] flex flex-col items-center justify-center'>
        <UserIcon />
        <div className='text-[12px] leading-[16px] my-[12px] text-center '>Connect with a CORT representative who can help you furnish your new home.</div>
          <div className="text-[12px] leading-[18px] text-[#EA002C] font-medium">
            Call CORT now at{' '}
            <a
              href="tel:8554090445"
              className="underline hover:text-[#b80022] transition-colors"
            >
              855-409-0445
            </a>
          </div>
          <div className='mt-[20px] text-[#3D4256] opacity-[50%] text-[12px] font-normal'>

          <a
            href="https://www.cort.com/privacy-legal"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-[#EA002C] transition-colors"
          >
            Privacy Policy
          </a>
          {' | '}
          <a
            href="https://www.cort.com"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-[#EA002C] transition-colors"
          >
            cort.com
          </a>  
          </div>
      </div>
      </VariantWrapper>
      {result && (
        <Result
          modal={isDesktop}
          result={result}
          onClose={() => {
            onClose(result)
            setResult(null)
          }}
        />
      )}
    </>
  )
}

export default ContactForm
